import { Service } from './models/Service.js';
import { Price } from './models/Price.js';
import {
  DATA_SHARING_PRODUCT_ID,
  DATA_SHARING_NAME,
  DATA_SHARING_GROUP_FEE,
  DATA_SHARING_MEMBERSHIP_FEE,
  DATA_MERGE_VARIANTS,
  DATA_MERGE_2SN_VARIANT,
  DATA_MERGE_NAME,
  M_HUB_CATEGORY_ID,
  API_CHARACTERISTIC_MEMBER,
  API_STATUS_PENDING_ACTIVATION,
  API_STATUS_CLOSED,
} from '../DataSharing/constants.js';
import { Category } from './models/Category.js';

const DATA_SHARING_SLUG = 'zdielam-svoje-data';
const DATA_SHARING_DESCRIPTION =
  'Ak máte na spoločnej faktúre viacero telefónnych čísel s dátovými balíkmi, môžete dáta z jedného z nich zdieľať na maximálne 3 ďalšie.';

const DATA_MERGE_SLUG = 'spojene-dat';
const DATA_MERGE_DESCRIPTION =
  'Spojte si dáta z viacerých svojich čísel a čerpajte z nich všetci spoločne ako z jedného veľkého balíka.';

const CATEGORY = 'Dátové balíky a služby';
const CATEGORY_ORDER = 40;

const STATUS_NOT_ELIGIBLE = 'not-eligible';
const STATUS_ELIGIBLE = 'eligible';
const STATUS_ACTIVE = 'active';
const STATUS_IN_PROGRESS = 'in-progress';
const STATUS_IN_PROGRESS_BILL_DAY = 'in-progress-bill-day';

/**
 * Parser for non-OST Data Sharing (SHS) services.
 *
 * @property {object} _config
 * @property {DataSharing} _dataSharing
 * @property {CustomerDataManager} _customerDataManager
 * @property {Logger} _logger
 */
export default class DataSharingParser {
  /**
   * @param {Object} config
   * @param {DataSharing} dataSharing
   * @param {CustomerDataManager} customerDataManager
   * @param {Logger} logger
   */
  constructor(config, dataSharing, customerDataManager, logger) {
    this._config = config;
    this._dataSharing = dataSharing;
    this._customerDataManager = customerDataManager;
    this._logger = logger;
  }

  /**
   * Add data sharing services to a basket
   *
   * Prepaid customers can't have data sharing services so we won't
   * augment their basket and perform redundant API calls.
   *
   * @param {Basket} basket
   * @returns {Promise<Basket>}
   */
  async augmentBasket(basket) {
    const { isPrepaid } = (await this._customerDataManager.getCustomerData()).cnData;
    if (isPrepaid) {
      return basket;
    }
    try {
      const { dataSharingService, dataMergeService } = await this._getDataSharingServices(
        basket.subscriptionId,
      );
      basket.services.push(dataSharingService);
      basket.services.push(dataMergeService);
      [dataSharingService, dataMergeService].forEach(service => {
        if (!basket.categoriesMap.has(service.category)) {
          basket.categoriesMap.set(
            service.category,
            new Category({
              name: service.category,
              order: CATEGORY_ORDER,
            }),
          );
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-param-reassign
      basket.isIncomplete = true;
      this._logger.warning(
        'SHS augmentation failed. Continuing with incomplete basket.',
        'infra-ost',
      );
    }
    return basket;
  }

  /**
   * Fetches necessary data and returns two non-OST services: "Zdielam svoje data" and "Spojenie dat"
   *
   * @param {string} subscriptionId
   * @returns {Promise<{dataMergeService: Service, dataSharingService: Service}>}
   */
  async _getDataSharingServices(subscriptionId) {
    const { dataSharingStatus, dataMergeStatus } = await this._getStatus(subscriptionId);
    const dataSharingPrice = await this._getDataSharingPrice(dataSharingStatus, subscriptionId);
    const dataMergePrice = await this._getDataMergePrice(dataMergeStatus, subscriptionId);

    const commonServiceParams = {
      customerOrderId: null,
      category: CATEGORY,
      mHubCategoryId: M_HUB_CATEGORY_ID,
      period: null,
      attributes: [],
      isVisible: true,
      canBeDeactivated: true,
      isSubsidy: false, // intentionally false to avoid services appearing in exchange journey
      isRecommended: true,
      isIdRcVerificationRequired: true,
      hasUnprocessedRequest: false,
      activationDate: null,
      isInitiallyActive: false,
      isActivating: false,
      isDeactivating: false,
      isAdded: false,
      isRemoved: false,
      isUpdated: false,
      isAddedInLastUpdate: false,
      isRemovedInLastUpdate: false,
      catalogType: null,
      tryAndBuy: null,
      hasActivateButton: false,
      hasDeactivateButton: false,
    };

    const dataSharingService = new Service({
      ...commonServiceParams,
      id: DATA_SHARING_PRODUCT_ID,
      uuid: DATA_SHARING_PRODUCT_ID,
      name: DATA_SHARING_NAME,
      slug: DATA_SHARING_SLUG,
      description: DATA_SHARING_DESCRIPTION,
      order: '1001',
      price: dataSharingPrice,
      originalPrice: dataSharingPrice,
      unitPrice: dataSharingPrice,
      changeDate: await this._getChangeDate(dataSharingStatus),
      isActive:
        dataSharingStatus === STATUS_ACTIVE ||
        dataSharingStatus === STATUS_IN_PROGRESS ||
        dataSharingStatus === STATUS_IN_PROGRESS_BILL_DAY,
      isEligible: dataSharingStatus === STATUS_ELIGIBLE,
      isInitiallyChanging:
        dataSharingStatus === STATUS_IN_PROGRESS ||
        dataSharingStatus === STATUS_IN_PROGRESS_BILL_DAY,
      customDetailLink: this._config.linkConfiguration.dataSharingPageLink,
    });

    const dataMergeService = new Service({
      ...commonServiceParams,
      id: DATA_MERGE_2SN_VARIANT.productId,
      uuid: DATA_MERGE_2SN_VARIANT.productId,
      name: DATA_MERGE_NAME,
      slug: DATA_MERGE_SLUG,
      description: DATA_MERGE_DESCRIPTION,
      order: '1002',
      price: dataMergePrice,
      originalPrice: dataMergePrice,
      unitPrice: dataMergePrice,
      changeDate: await this._getChangeDate(dataMergeStatus),
      isActive:
        dataMergeStatus === STATUS_ACTIVE ||
        dataMergeStatus === STATUS_IN_PROGRESS ||
        dataMergeStatus === STATUS_IN_PROGRESS_BILL_DAY,
      isEligible: dataMergeStatus === STATUS_ELIGIBLE,
      isInitiallyChanging:
        dataMergeStatus === STATUS_IN_PROGRESS || dataMergeStatus === STATUS_IN_PROGRESS_BILL_DAY,
      customDetailLink: this._config.linkConfiguration.dataMergePageLink,
    });

    return { dataSharingService, dataMergeService };
  }

  /**
   * Compiles active/eligible status for both services.
   *
   * @param {string} subscriptionId
   * @returns {Promise<{dataSharingStatus: string, dataMergeStatus: string}>}
   * @private
   */
  async _getStatus(subscriptionId) {
    // Customers who are not authorized with ID code can't interact with data sharing services.
    const { isCustomerAdmin } = (await this._customerDataManager.getCustomerData()).cnData;
    if (!isCustomerAdmin) {
      return {
        dataSharingStatus: STATUS_NOT_ELIGIBLE,
        dataMergeStatus: STATUS_NOT_ELIGIBLE,
      };
    }

    return {
      dataSharingStatus: await this._getDataSharingStatus(subscriptionId),
      dataMergeStatus: await this._getDataMergeStatus(subscriptionId),
    };
  }

  /**
   * Determine status for data sharing service
   *
   * @param {string} subscriptionId
   * @returns {Promise<string>}
   * @private
   */
  async _getDataSharingStatus(subscriptionId) {
    const { isDataSharingActive, isInProgress, isCancellable } =
      await this._fetchActiveServices(subscriptionId);

    if (isDataSharingActive) {
      if (isInProgress) {
        return isCancellable ? STATUS_IN_PROGRESS_BILL_DAY : STATUS_IN_PROGRESS;
      }
      return STATUS_ACTIVE;
    }

    const isDataSharingEligible = await this._fetchServiceEligibility(
      subscriptionId,
      DATA_SHARING_PRODUCT_ID,
    );

    return isDataSharingEligible ? STATUS_ELIGIBLE : STATUS_NOT_ELIGIBLE;
  }

  /**
   * Determine status for data merge service
   *
   * @param {string} subscriptionId
   * @returns {Promise<string>}
   * @private
   */
  async _getDataMergeStatus(subscriptionId) {
    const { isDataMergeActive, isInProgress, isCancellable } =
      await this._fetchActiveServices(subscriptionId);

    if (isDataMergeActive) {
      if (isInProgress) {
        return isCancellable ? STATUS_IN_PROGRESS_BILL_DAY : STATUS_IN_PROGRESS;
      }
      return STATUS_ACTIVE;
    }

    // Only checking eligibility for the base 2 SN version of the service, since that's enough to grant access to
    // the data sharing detail screen
    const isDataMergeEligible = await this._fetchServiceEligibility(
      subscriptionId,
      DATA_MERGE_2SN_VARIANT.productId,
    );

    return isDataMergeEligible ? STATUS_ELIGIBLE : STATUS_NOT_ELIGIBLE;
  }

  /**
   * Fetches information about active services.
   *
   * @param {string} subscriptionId
   * @returns {Promise<{isDataSharingActive: boolean, isDataMergeActive: boolean, isInProgress: boolean, isCancellable: boolean}>}
   * @private
   */
  async _fetchActiveServices(subscriptionId) {
    const data = await this._dataSharing.getCustomerProduct(subscriptionId, true);

    if (data === null) {
      return { isDataSharingActive: false, isDataMergeActive: false };
    }

    const productId = data.productSpecification?.id;

    return {
      isDataSharingActive: productId === DATA_SHARING_PRODUCT_ID,
      isDataMergeActive: DATA_MERGE_VARIANTS.some(variant => variant.productId === productId),
      isInProgress:
        data.status === API_STATUS_PENDING_ACTIVATION || data.status === API_STATUS_CLOSED,
      isCancellable:
        data.characteristic.find(item => item.name === 'arePendingChangesCancellable')?.value ??
        false,
    };
  }

  /**
   * Checks whether the service is eligible
   *
   * @param {string} subscriptionId
   * @param {string} productId
   * @returns {Promise<boolean>}
   * @private
   */
  async _fetchServiceEligibility(subscriptionId, productId) {
    const data = await this._dataSharing.getProductOffering({
      subscriptionId,
      productId,
      isNonessential: true,
    });

    if (data === null) {
      return false;
    }

    return (
      data.shsContributorList.filter(item => item.subscriptionId === subscriptionId).length > 0
    );
  }

  /**
   * Get price for Data Sharing service.
   *
   * @param {string} status
   * @param {string} subscriptionId
   * @returns {Promise<Price>}
   * @private
   */
  async _getDataSharingPrice(status, subscriptionId) {
    if (status === STATUS_ELIGIBLE || status === STATUS_NOT_ELIGIBLE) {
      return new Price(DATA_SHARING_GROUP_FEE);
    }

    const data = await this._dataSharing.getCustomerProduct(subscriptionId, true);
    const numberOfMembers = data.characteristic.filter(
      item => item.name === API_CHARACTERISTIC_MEMBER,
    ).length;

    return new Price(numberOfMembers * DATA_SHARING_MEMBERSHIP_FEE);
  }

  /**
   * Get price for Data Merge service.
   *
   * @param {string} status
   * @param {string} subscriptionId
   * @returns {Promise<Price>}
   * @private
   */
  async _getDataMergePrice(status, subscriptionId) {
    if (status === STATUS_ELIGIBLE || status === STATUS_NOT_ELIGIBLE) {
      return new Price(DATA_MERGE_2SN_VARIANT.groupFee);
    }
    const data = await this._dataSharing.getCustomerProduct(subscriptionId, true);
    const price = DATA_MERGE_VARIANTS.find(
      variant => variant.productId === data.productSpecification?.id,
    ).groupFee;
    return new Price(price);
  }

  /**
   * @param {string} status
   * @return {Promise<?string>}
   * @private
   */
  async _getChangeDate(status) {
    const billDay = await this._customerDataManager.getNextBillingPeriodStartDate();
    const sysDay = new Date().toISOString();

    if (status === STATUS_IN_PROGRESS) {
      return sysDay;
    }

    if (status === STATUS_IN_PROGRESS_BILL_DAY) {
      return billDay;
    }

    return null;
  }
}
